/**
 * apiEndpoints — EVERY backend path the app calls, in one place.
 *
 * Why: when the backend renames or moves an endpoint, this file is the only
 * change. API files import from here and never write a path string of their
 * own (rule: .claude/rules/state.md, "API endpoints").
 *
 * Grouped by the PAGE (or app-wide area) that uses them, so finding "what does
 * Manage Portfolio call?" is one glance.
 *
 * Paths only — no host. The host comes from env (config/env.js):
 *  - the advisory API (VITE_API_BASE_URL) is the default base for every request;
 *  - Common Services (VITE_CS_API) paths are marked CS and joined with
 *    env.csApiBaseUrl by the API file that uses them.
 * Query-string values are passed as `params` by the caller, never baked in here.
 */

export const API_ENDPOINTS = Object.freeze({
  /** Sign-in screens (features/auth) and the session layer (services). */
  AUTH: Object.freeze({
    LOGIN: '/login',
    /** CS — on the Common Services host. */
    GET_USER_TOKEN: '/getUserToken',
    /** CS — a new token for an expired one (services/sessionRefresh.js). */
    REFRESH_TOKEN: '/getRefreshToken',
  }),

  /** App bar — the notifications bell, on every page. */
  APP_BAR: Object.freeze({
    NOTIFICATIONS: '/notifications',
    NOTIFICATION_READ: '/notifications/read',
    NOTIFICATIONS_CLEAR_ALL: '/notifications/del',
  }),

  /** Manage Portfolio — the sidebar list and the add-instance form. */
  MANAGE_PORTFOLIO: Object.freeze({
    /** The sidebar list (list_all=true); also loads one portfolio, with other params. */
    PORTFOLIOS: '/get-portfolio',
    /** Progress of a big portfolio still being processed (sidebar refresh icon). */
    JOB_STATUS: '/job-status',
    /** Region options for a provider. */
    REGIONS: '/regions',
    /** Instance Type options for a provider + region. */
    INSTANCE_SIZES: '/instance-sizes',
    /** Server check of instance rows before they join the portfolio (Add Instance, table edits). */
    VALIDATE_INSTANCES: '/input/validate',
    /** Find and Replace: the server swaps values across every row and re-checks them. */
    CORRECT_INSTANCES: '/input/correct',
    /** EVERY instance type of a provider, any region (Find and Replace "To" list). */
    CLOUD_INSTANCES: '/cloud-instances',

    /* --- file upload (in call order) --- */
    /** Creates the portfolio and returns a storage link for the file (and, with udf_file, for the self-perf file). */
    UPLOAD_LINK: '/upload-portfolio',
    /** How many rows the uploaded file has — decides normal vs. background processing. */
    EXCEL_ROW_COUNT: '/get-excel-rowcount',
    /** Reads the uploaded file(s): instance rows (Data) and self-perf rows (UDF). */
    VALIDATE_UPLOAD: '/file-upload/validate',
    /** Big files: process the portfolio in the background instead. */
    INITIATE_COST_ADVICE: '/initiate-costAdvice',
    /** Removes a portfolio — also used to undo a half-finished upload. */
    DELETE_PORTFOLIO: '/delete-portfolio',

    /* --- the Save button --- */
    /** A new portfolio. */
    SAVE_PORTFOLIO: '/save-portfolio',
    /** An existing one (e.g. created by an upload), by ?id=. */
    UPDATE_PORTFOLIO: '/update-portfolio',
  }),

  /**
   * Instance Advice (EIA) / Cost Advice (CCA) — the recommendations for a saved
   * portfolio (legacy apiservice.js costAdvice / largeFileRecommendations).
   */
  COST_ADVICE: Object.freeze({
    /** The advice for a normal portfolio, a page at a time; also Regenerate. */
    COST_ADVICE: '/cost-advice',
    /** The same for a General IT Average ("scaler") portfolio. */
    SCALER_COST_ADVICE: '/scaler-cost-advice',
    /** A large-data portfolio's advice, already worked out in the background. */
    LATEST_RECOMMENDATIONS: '/latest-recommendations',
    /** Counts a PPT / Excel download (analytics); sent before the file opens. */
    DOWNLOAD_COUNT: '/download-count',
  }),

  /**
   * Explorer (CCA) — the price list of a provider's AMD instances (legacy
   * apiservice.js instanceListByProvider). Its Region options come from
   * MANAGE_PORTFOLIO.REGIONS, the same request and cache as Manage Portfolio.
   */
  EXPLORER: Object.freeze({
    /** GET ?provider=&email=&os_type= -> { Data: [...], os_type }. */
    INSTANCES: '/explorer',
  }),

  /** Regions list — every region of a provider and its instance types (opens in a new tab). */
  REGIONS_LIST: Object.freeze({
    INSTANCES_SUMMARY: '/instances/summary',
  }),

  /**
   * Telemetry — a connector (Datadog, CloudWatch, …) as a portfolio's source.
   * Each takes the connector's slug as its last path segment:
   * `${CONNECTION}/datadog` (legacy apiservice.js :582-606).
   */
  TELEMETRY: Object.freeze({
    /** Check the connector's credentials work. */
    CONNECTION: '/telemetry/connection',
    /** Create the portfolio and start reading its instances in the background. */
    METRICS: '/telemetry/metrics',
  }),

  /**
   * Cloud Usage Reports (CCA) — a portfolio made from a cloud account's
   * instances (legacy apiservice.js CloudConnection / AddCloudConnection
   * :382-457). The account's secrets travel with each call.
   */
  CLOUD_USAGE_REPORTS: Object.freeze({
    /** POST: add the account — the server reads its instances into a portfolio. */
    ACCOUNTS: '/cloud-accounts',
    /** GET: check the account's credentials work (legacy query_type test_account). */
    TEST_ACCOUNT: '/cloud-accounts/test_account',
  }),

  /**
   * Analytics v2 — the Business Analytics Dashboard (legacy apiservice.js
   * :993-1292). Two hosts: unmarked paths are on the advisory API; CS paths
   * are on Common Services and joined with env.csApiBaseUrl by the API file.
   *
   * `/organizations` exists on BOTH hosts with different meanings, hence two
   * distinct keys: ORGANISATIONS (advisory — active users + totals for the
   * Organization section) and CS_ORGANIZATIONS_LIST (CS — the header's ORG
   * options). Regions for Top No-Reco reuse MANAGE_PORTFOLIO.REGIONS.
   */
  ANALYTICS: Object.freeze({
    /** KPI Summary (legacy getRecommendedInstancesSummary :1110). */
    RECOMMENDED_INSTANCES_SUMMARY: '/recommended-instances-summary',
    /** Organization section (legacy getOrganisations :1092). */
    ORGANISATIONS: '/organizations',
    /** EIA vs CCA method reconciliation (legacy :1020). */
    METHOD_RECONCILIATION: '/method-reconciliation',
    /** Annual savings by organization (legacy :1056). */
    SAVINGS: '/savings',
    /** Recommendation funnel + org/CSP tables (legacy :1038). */
    RECOMMENDATION_FUNNEL: '/recommendation-funnel',
    /** Saving type / instance types with no recommendation (legacy :1074). */
    INSTANCES_WITH_NO_RECOMMENDATIONS: '/instances-with-no-recommendations',
    /** MAU trend (trend_months) and Stickiness month (month) (legacy :1172, :1128). */
    ORGANIZATION_ACTIVATION: '/organization-activation',
    /** Performance improvement bands (legacy :1223). */
    PERFORMANCE_IMPROVEMENT: '/organization-activation/performance-improvement',
    /** 12-week upload trend (legacy :1189). */
    UPLOAD_TREND: '/upload-trend',
    /** Top no-recommendation instances per cloud (legacy :1240). */
    TOP_NO_RECOMMENDATION_INSTANCES: '/top-no-recommendation-instances',

    /** CS — the header's ORG options (legacy getOrganizationsList :993). */
    CS_ORGANIZATIONS_LIST: '/organizations',
    /** CS — Engagement app chips (legacy getApplications :1162). */
    CS_APPLICATIONS: '/applications',
    /** CS — Engagement heatmap/bar/table (legacy :1145). */
    CS_ORGANIZATION_ENDPOINT_USAGE: '/organization-endpoint-usage',
    /** CS — Feedback KPI cards (legacy :1260). */
    CS_FEEDBACK_SUBMISSION_OVERVIEW: '/feedbacksubmissionoverview',
    /** CS — Feedback trend lines + donut (legacy :1277). */
    CS_FEEDBACK_TRENDS: '/feedbacktrends',
  }),
});
